import { Injectable } from '@angular/core';
import { AdsPlateform, SiteSettings } from '../dto/response/site.settings';

@Injectable({
  providedIn: 'root'
})
export class PixelService {

  facebookPixelExist: boolean = false;
  tiktokPixelExist: boolean = false;
  // --- GOOGLE ---
  googleTagLoaded = false;
  googleAnalyticsExist = false; // interne ou custom
  googleAdsExist = false;

  private internalAnalyticsId?: string;  // internal_analytics_id
  private customAnalyticsId?: string;    // custom_analytics_id (optionnel)
  private googleAdsIds: string[] = [];   // pixels platform = GOOGLE
  private siteIdentifier?: string;       // siteId ou DNS
  constructor() { }


  // Load Facebook Pixel dynamically
  loadFacbookPixel(pixelId: string): void {
    // Include the Facebook Pixel base code
    this.facebookPixelExist = true;

    (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return; n = f.fbq = function () {
        n.callMethod ?
          n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
      n.queue = []; t = b.createElement(e); t.async = !0;
      t.src = v; s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s)
    })(window, document, 'script',
      'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');

  }

  // Load TikTok Pixel dynamically
  loadTiktokPixel(pixelId: string): void {
    this.tiktokPixelExist = true;

    (function (w: any, d: any, t: string) {
      w.TiktokAnalyticsObject = t;
      const ttq = w[t] = w[t] || [];
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
      ttq.setAndDefer = function (obj: any, method: string) {
        obj[method] = function () {
          obj.push([method].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      for (let i = 0; i < ttq.methods.length; i++) {
        ttq.setAndDefer(ttq, ttq.methods[i]);
      }
      ttq.load = function (id: string) {
        const script = d.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = "https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=" + id + "&lib=" + t;
        const firstScript = d.getElementsByTagName("script")[0];
        firstScript.parentNode.insertBefore(script, firstScript);
      };
      ttq.load(pixelId);
      ttq.page();
    })(window, document, 'ttq');
  }


  // ============ INIT GOOGLE ============

  initGoogleTags(settings: SiteSettings, siteId: string): void {
    const analytics = settings.analytics;
    const pixels = settings.ads_manager?.pixels || [];

    this.siteIdentifier = siteId;

    // Analytics : interne toujours présent, custom optionnel
    this.internalAnalyticsId = analytics?.internal_analytics_id || undefined;
    this.customAnalyticsId = analytics?.custom_analytics_id || undefined;

    this.googleAnalyticsExist = !!this.internalAnalyticsId || !!this.customAnalyticsId;

    // Google Ads (ou GTAG) : via ads_manager.pixels
    this.googleAdsIds = pixels.filter(p => p.platform === AdsPlateform.GOOGLE && !!p.id)
      .map(p => p.id);

    this.googleAdsExist = this.googleAdsIds.length > 0;

    const idsToLoad = [
      this.internalAnalyticsId,
      this.customAnalyticsId,
      ...this.googleAdsIds,
    ].filter(Boolean) as string[];

    if (!idsToLoad.length || this.googleTagLoaded) {
      return;
    }

    // Bootstrap gtag une seule fois
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer.push(arguments as any);
      } as any;
    }

    const firstId = idsToLoad[0]; // utilisé pour charger le script
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(firstId)}`;
    document.head.appendChild(s);

    window.gtag('js', new Date());

    // config GA4 interne + GA4 custom + éventuels IDs Ads
    idsToLoad.forEach(id => {
      if (id === this.internalAnalyticsId) {
        window.gtag('config', id, {
          site_id: this.siteIdentifier,          // ton siteId
          site_domain: window.location.hostname, // pour filtrer côté BigQuery / GA
        });
      } else {
        window.gtag('config', id);
      }
    });

    this.googleTagLoaded = true;
  }

  /**
   * Envoie un event GA4 vers :
   *  - internal_analytics_id (avec site_id / site_domain)
   *  - custom_analytics_id (si présent)
   */
  trackGoogleAnalytics(eventName: string, params: any): void {
    if (!this.googleAnalyticsExist || !window.gtag) return;

    // Interne : on injecte systématiquement site_id / site_domain
    if (this.internalAnalyticsId) {
      window.gtag('event', eventName, {
        ...params,
        site_id: this.siteIdentifier,
        site_domain: window.location.hostname,
        send_to: this.internalAnalyticsId,
      });
    }

    // Client : on renvoie le payload brut
    if (this.customAnalyticsId) {
      window.gtag('event', eventName, {
        ...params,
        send_to: this.customAnalyticsId,
      });
    }
  }

  // ============ TRACK GOOGLE ADS CONVERSION ============

  /**
   * Envoie une conversion vers tous les pixels GOOGLE (ads_manager.pixels)
   * Hypothèse : id déjà au bon format :
   *  - "AW-XXXXXXX" ou "AW-XXXXXXX/LABEL"
   */
  trackGoogleAdsConversion(params: { value: number; currency: string; transaction_id?: string }): void {
    if (!this.googleAdsExist || !window.gtag) return;

    this.googleAdsIds.forEach(id => {
      window.gtag('event', 'conversion', {
        send_to: id,
        value: params.value,
        currency: params.currency,
        transaction_id: params.transaction_id,
      });
    });
  }



  // Track custom events
  trackFbPixel(event: string, data?: any) {
    if ((window as any).fbq) {
      window.fbq('track', event, data);
    }
  }
  trackTiktokPixel(event: string, data?: any) {
    if (!(window as any).ttq) return;
    window.ttq.track(event, data);
  }







}
